import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useApiClient } from '@/hooks/useApiClient'
import { useTheme } from '@/hooks/useTheme'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'
import { formatDateDisplay } from '@/utils/dateHelpers'
import { getStatusColors } from '@/utils/statusColorMap'
import { toastError, toastSuccess, toastInfo } from '@/utils/toastHelper'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { ActionButton, ConfirmDeleteModal } from '@/components/ui'

interface PeriodoNoDisponible {
  id: string
  espacio_id: string
  empresa_id: number
  fecha_inicio: string
  fecha_fin: string
  razon: string
  descripcion: string | null
  activo: boolean
  fecha_creacion: string
  fecha_actualizacion: string
}

interface Espacio {
  id: string
  codigo: string
  nombre: string
}

interface PeriodoForm {
  espacio_id: string
  fecha_inicio: string
  fecha_fin: string
  razon: string
  descripcion: string
}

const RAZONES = ['mantenimiento', 'limpieza', 'privado', 'reparación', 'otro']

export default function BloqueosAdmin() {
  const { tienePermiso, usuario } = useAuth()
  const { apiClient, loading, error, setLoading, setError } = useApiClient()
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()

  // Estado de períodos
  const [periodos, setPeriodos] = useState<PeriodoNoDisponible[]>([])
  const [espacios, setEspacios] = useState<Espacio[]>([])

  // Estado del formulario
  const [showForm, setShowForm] = useState(false)
  const [editingPeriodo, setEditingPeriodo] = useState<PeriodoNoDisponible | null>(null)
  const [form, setForm] = useState<PeriodoForm>({
    espacio_id: '',
    fecha_inicio: '',
    fecha_fin: '',
    razon: 'mantenimiento',
    descripcion: '',
  })

  // Filtros
  const [filtroEspacio, setFiltroEspacio] = useState('')
  const [filtroRazon, setFiltroRazon] = useState('')
  const [filtroActivo, setFiltroActivo] = useState<boolean | null>(null)

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ id: string; nombre: string } | null>(null)

  // Permisos
  const hasPermission = (permission: string) => tienePermiso(permission)
  const puedeVer = hasPermission('bloqueos.ver')
  const puedeCrear = hasPermission('bloqueos.crear')
  const puedeEditar = hasPermission('bloqueos.actualizar')
  const puedeEliminar = hasPermission('bloqueos.eliminar')

  // Cargar espacios y períodos
  const fetchEspacios = async () => {
    try {
      const response = await apiClient.get('/api/admin-protected/espacios')
      const data = response.data || []
      setEspacios(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error cargando espacios:', err)
    }
  }

  const fetchTodosPeriodos = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/api/admin-protected/bloqueos')
      const data = response.data || []
      setPeriodos(Array.isArray(data) ? data : [])
      setError(null)
    } catch (err: any) {
      console.error('Error cargando períodos:', err)
      setError(err.response?.data?.detail || 'Error al cargar períodos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true)
        // Cargar espacios y períodos en paralelo
        const [espaciosResponse, periodosResponse] = await Promise.all([
          apiClient.get('/api/admin-protected/espacios'),
          apiClient.get('/api/admin-protected/bloqueos')
        ])

        const espaciosArray = Array.isArray(espaciosResponse.data) ? espaciosResponse.data : []
        const periodosArray = Array.isArray(periodosResponse.data) ? periodosResponse.data : []

        // Actualizar estado solo cuando ambos hayan cargado
        setEspacios(espaciosArray)
        setPeriodos(periodosArray)
        setError(null)
      } catch (err: any) {
        console.error('Error cargando datos iniciales:', err)
        setError(err.response?.data?.detail || 'Error al cargar datos')
      } finally {
        setLoading(false)
      }
    }
    loadInitialData()
  }, [usuario?.empresa_activa?.id])

  // Reset del formulario
  const resetForm = () => {
    setForm({
      espacio_id: '',
      fecha_inicio: '',
      fecha_fin: '',
      razon: 'mantenimiento',
      descripcion: '',
    })
    setEditingPeriodo(null)
  }

  const closeForm = () => {
    setShowForm(false)
    resetForm()
  }

  // Crear o editar período
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.espacio_id || !form.fecha_inicio || !form.fecha_fin) {
      toastError('Por favor completa todos los campos requeridos')
      return
    }

    const fechaInicio = new Date(form.fecha_inicio)
    const fechaFin = new Date(form.fecha_fin)

    if (fechaFin < fechaInicio) {
      toastError('La fecha fin no puede ser anterior a la fecha inicio')
      return
    }

    const espacioId = form.espacio_id

    try {
      const payload = {
        espacio_id: form.espacio_id,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin,
        razon: form.razon,
        descripcion: form.descripcion || null,
      }

      if (editingPeriodo) {
        await apiClient.put(`/api/admin-protected/bloqueos/${editingPeriodo.id}`, payload)
        toastSuccess('✅ Período actualizado')
      } else {
        await apiClient.post('/api/admin-protected/bloqueos', payload)
        toastSuccess('✅ Período creado')
      }

      closeForm()
      // Recargar la lista de períodos
      await fetchTodosPeriodos()
    } catch (err: any) {
      const message = err.response?.data?.detail || (err instanceof Error ? err.message : 'Error al guardar')
      toastError(`❌ ${message}`)
      console.error('❌ Error:', message)
    }
  }

  // Editar período
  const handleEdit = (periodo: PeriodoNoDisponible) => {
    setEditingPeriodo(periodo)
    setForm({
      espacio_id: periodo.espacio_id,
      fecha_inicio: periodo.fecha_inicio,
      fecha_fin: periodo.fecha_fin,
      razon: periodo.razon,
      descripcion: periodo.descripcion || '',
    })
    setShowForm(true)
  }

  // Eliminar período
  const handleDelete = (id: string, nombre: string) => {
    setItemToDelete({ id, nombre })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return

    try {
      await apiClient.delete(`/api/admin-protected/bloqueos/${itemToDelete.id}`)
      toastSuccess('✅ Período eliminado')
      setShowDeleteConfirm(false)
      setItemToDelete(null)
      // Recargar la lista de períodos
      await fetchTodosPeriodos()
    } catch (err: any) {
      const message = err.response?.data?.detail || (err instanceof Error ? err.message : 'Error al eliminar')
      toastError(`❌ ${message}`)
    }
  }

  // Filtrar períodos
  const periodosFiltrados = periodos.filter((p) => {
    if (filtroRazon && p.razon !== filtroRazon) return false
    if (filtroActivo !== null && p.activo !== filtroActivo) return false
    return true
  })

  if (!puedeVer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Sin permisos</h3>
          <p className="text-gray-600">No tienes permisos para ver los bloqueos</p>
        </div>
      </div>
    )
  }

  if (loading && periodos.length === 0 && filtroEspacio) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando períodos..." />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Períodos No Disponibles</h2>
                <p className="text-gray-600">Gestiona los bloqueos de espacios (mantenimiento, limpieza, etc.)</p>
              </div>
              <div>
                {puedeCrear && (
                  <button
                    onClick={() => {
                      resetForm()
                      setShowForm(true)
                    }}
                    className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primaryDark
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary
                    }}
                  >
                    🔒 Crear Período
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
            <div className={`grid gap-4 ${deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Razón</label>
                <select
                  value={filtroRazon}
                  onChange={(e) => setFiltroRazon(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: colors.primary }}
                >
                  <option value="">Todas las razones</option>
                  {RAZONES.map((r) => (
                    <option key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <select
                  value={filtroActivo === null ? '' : String(filtroActivo)}
                  onChange={(e) => setFiltroActivo(e.target.value === '' ? null : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: colors.primary }}
                >
                  <option value="">Todos los estados</option>
                  <option value="true">Activos</option>
                  <option value="false">Inactivos</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFiltroRazon('')
                    setFiltroActivo(null)
                  }}
                  className="w-full px-4 py-2 rounded-lg font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                  style={{ backgroundColor: colors.primaryDark }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1'
                  }}
                >
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {/* Tabla de Períodos */}
          {!loading && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Espacio</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inicio</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fin</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {periodosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                          No hay períodos no disponibles registrados
                        </td>
                      </tr>
                    ) : (
                      periodosFiltrados.map((periodo, idx) => {
                        const espacio = espacios.find((e) => e.id === periodo.espacio_id)
                        return (
                          <tr key={periodo.id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{espacio?.codigo}</div>
                              <div className="text-xs text-gray-600">{espacio?.nombre}</div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatDateDisplay(periodo.fecha_inicio)}</div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatDateDisplay(periodo.fecha_fin)}</div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span
                                className="px-2 py-1 rounded-full text-xs font-semibold text-white"
                                style={{ backgroundColor: colors.primary }}
                              >
                                {periodo.razon}
                              </span>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-center">
                              {periodo.activo ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold" style={{
                                  backgroundColor: `${colors.primary}20`,
                                  color: colors.primary
                                }}>
                                  ✓ Activo
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                                  ✕ Inactivo
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-center">
                              <div className="flex gap-1 justify-center">
                                {puedeEditar && (
                                  <ActionButton
                                    type="edit"
                                    onClick={() => handleEdit(periodo)}
                                    title="Editar período"
                                  />
                                )}
                                {puedeEliminar && (
                                  <ActionButton
                                    type="delete"
                                    onClick={() => handleDelete(periodo.id, `${espacio?.codigo} - ${espacio?.nombre}`)}
                                    title="Eliminar período"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Formulario */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-90vh overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingPeriodo ? '✏️ Editar Período' : '🔒 Crear Período No Disponible'}
              </h2>
              <button
                onClick={closeForm}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Espacio */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Espacio *
                </label>
                <select
                  value={form.espacio_id}
                  onChange={(e) => setForm({ ...form, espacio_id: e.target.value })}
                  disabled={!!editingPeriodo}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: colors.primary }}
                >
                  <option value="">Selecciona un espacio</option>
                  {espacios.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.codigo} - {e.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fechas */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Fecha Inicio *
                  </label>
                  <input
                    type="date"
                    value={form.fecha_inicio}
                    onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ focusRingColor: colors.primary }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Fecha Fin *
                  </label>
                  <input
                    type="date"
                    value={form.fecha_fin}
                    onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ focusRingColor: colors.primary }}
                  />
                </div>
              </div>

              {/* Razón */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Razón *
                </label>
                <select
                  value={form.razon}
                  onChange={(e) => setForm({ ...form, razon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: colors.primary }}
                >
                  {RAZONES.map((r) => (
                    <option key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Descripción (opcional)
                </label>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Notas adicionales sobre el período..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none"
                  style={{ focusRingColor: colors.primary }}
                  rows={3}
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-4 py-3 rounded-lg font-semibold text-gray-900 bg-gray-200 transition-all duration-300 ease-out"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d1d5db'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all duration-300 ease-out"
                  style={{
                    backgroundColor: loading ? `${colors.primary}80` : colors.primary,
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = colors.primaryDark
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = colors.primary
                    }
                  }}
                >
                  {loading ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        title="¿Eliminar período?"
        message="¿Estás seguro que deseas eliminar este período no disponible?"
        itemName={itemToDelete?.nombre}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setItemToDelete(null)
        }}
        isLoading={loading}
      />
    </div>
  )
}
